import hiddenEffectDocumentJson from "../content/hidden-effects.json";
import workshopOverrideJson from "../content/skill-overrides.json";
import { isAllowedAttackIntervalMs } from "./attack-interval";
import { isAllowedSkillRange } from "./skill-range";
import type {
  HiddenEffectDefinition,
  HiddenSynergyDefinition,
} from "./phase-three-types";

interface HiddenEffectDocument {
  version: 1;
  hiddenEffects: HiddenEffectDefinition[];
  hiddenSynergies: HiddenSynergyDefinition[];
}

const document = hiddenEffectDocumentJson as HiddenEffectDocument;

export const DEFAULT_HIDDEN_EFFECTS: HiddenEffectDefinition[] =
  document.hiddenEffects.map((effect) => ({
    ...effect,
    repeatable: effect.repeatable ? { ...effect.repeatable } : undefined,
    effects: effect.effects.map((runtimeEffect) => ({ ...runtimeEffect })),
  }));

export const DEFAULT_HIDDEN_SYNERGIES: HiddenSynergyDefinition[] =
  document.hiddenSynergies.map((synergy) => ({
    ...synergy,
    requiredEffectIds: [...synergy.requiredEffectIds],
    activatesEffectIds: [...synergy.activatesEffectIds],
    repeatableRequirement: synergy.repeatableRequirement
      ? { ...synergy.repeatableRequirement }
      : undefined,
    suppressesSynergyIds: [...(synergy.suppressesSynergyIds ?? [])],
    effects: synergy.effects.map((runtimeEffect) => ({ ...runtimeEffect })),
  }));

export const HIDDEN_EFFECTS: HiddenEffectDefinition[] = applyOverrides(
  DEFAULT_HIDDEN_EFFECTS,
  "hiddenEffects",
);

export const HIDDEN_SYNERGIES: HiddenSynergyDefinition[] = applyOverrides(
  DEFAULT_HIDDEN_SYNERGIES,
  "hiddenSynergies",
);

const hiddenEffectById = new Map(
  HIDDEN_EFFECTS.map((effect) => [effect.id, effect]),
);
const hiddenSynergyById = new Map(
  HIDDEN_SYNERGIES.map((synergy) => [synergy.id, synergy]),
);

export function getHiddenEffect(id: string): HiddenEffectDefinition {
  const effect = hiddenEffectById.get(id);
  if (!effect) {
    throw new Error(`Unknown hidden effect: ${id}`);
  }
  return effect;
}

export function getHiddenSynergy(id: string): HiddenSynergyDefinition {
  const synergy = hiddenSynergyById.get(id);
  if (!synergy) {
    throw new Error(`Unknown hidden synergy: ${id}`);
  }
  return synergy;
}

export function evaluateHiddenSynergies(
  acquiredEffectIds: Iterable<string>,
): HiddenSynergyDefinition[] {
  const acquired = new Set(acquiredEffectIds);
  return HIDDEN_SYNERGIES.filter(
    (synergy) =>
      !synergy.repeatableRequirement &&
      synergy.requiredEffectIds.every((id) => acquired.has(id)),
  );
}

export function getActiveHiddenEffectIds(
  acquiredEffectIds: Iterable<string>,
  activeSynergyIds: Iterable<string>,
): Set<string> {
  const acquired = new Set(acquiredEffectIds);
  const active = new Set(
    [...acquired].filter((id) => !getHiddenEffect(id).startsLocked),
  );
  for (const synergyId of activeSynergyIds) {
    for (const effectId of getHiddenSynergy(synergyId).activatesEffectIds) {
      if (acquired.has(effectId)) {
        active.add(effectId);
      }
    }
  }
  return active;
}

export function validateHiddenEffectContent(): string[] {
  const errors: string[] = [];
  const effectIds = new Set<string>();
  const synergyIds = new Set<string>();
  for (const effect of HIDDEN_EFFECTS) {
    if (effectIds.has(effect.id)) {
      errors.push(`重复隐藏效果ID: ${effect.id}`);
    }
    effectIds.add(effect.id);
    if (effect.repeatable && effect.startsLocked) {
      errors.push(`可重复隐藏效果 ${effect.id} 不得初始锁定`);
    }
    for (const runtime of effect.effects) {
      errors.push(
        ...validateHiddenRuntimeEffect(runtime).map(
          (message) => `隐藏效果 ${effect.id}: ${message}`,
        ),
      );
    }
  }
  for (const synergy of HIDDEN_SYNERGIES) {
    if (synergyIds.has(synergy.id)) {
      errors.push(`重复隐藏羁绊ID: ${synergy.id}`);
    }
    synergyIds.add(synergy.id);
    for (const effectId of [
      ...synergy.requiredEffectIds,
      ...synergy.activatesEffectIds,
    ]) {
      if (!effectIds.has(effectId)) {
        errors.push(`隐藏羁绊 ${synergy.id} 引用了未知隐藏效果 ${effectId}`);
      }
    }
    const requirement = synergy.repeatableRequirement;
    if (requirement) {
      const effect = HIDDEN_EFFECTS.find(
        (candidate) => candidate.id === requirement.effectId,
      );
      if (!effect?.repeatable) {
        errors.push(
          `计数型隐藏羁绊 ${synergy.id} 引用了不可重复隐藏效果 ${requirement.effectId}`,
        );
      }
      if (!Number.isInteger(requirement.count) || requirement.count <= 0) {
        errors.push(`计数型隐藏羁绊 ${synergy.id} 的阈值无效`);
      }
      if (
        requirement.minSourceTier !== undefined &&
        requirement.exactSourceTier !== undefined
      ) {
        errors.push(`计数型隐藏羁绊 ${synergy.id} 不能同时配置最低与精确品阶`);
      }
      if (
        requirement.activationBoundary !== "after_prerequisite" ||
        requirement.activationGrantCarriesForward !== false
      ) {
        errors.push(`计数型隐藏羁绊 ${synergy.id} 必须使用严格的阶段后边界`);
      }
      if (
        requirement.afterSynergyId &&
        !HIDDEN_SYNERGIES.some(
          (candidate) => candidate.id === requirement.afterSynergyId,
        )
      ) {
        errors.push(
          `计数型隐藏羁绊 ${synergy.id} 引用了未知前置 ${requirement.afterSynergyId}`,
        );
      }
    }
    for (const suppressedId of synergy.suppressesSynergyIds ?? []) {
      if (
        suppressedId === synergy.id ||
        !HIDDEN_SYNERGIES.some((candidate) => candidate.id === suppressedId)
      ) {
        errors.push(`隐藏羁绊 ${synergy.id} 的替换目标 ${suppressedId} 无效`);
      }
    }
    for (const effect of synergy.effects) {
      errors.push(
        ...validateHiddenRuntimeEffect(effect).map(
          (message) => `隐藏羁绊 ${synergy.id}: ${message}`,
        ),
      );
      if (
        effect.kind === "periodic_random_damage" &&
        (!isAllowedAttackIntervalMs(effect.intervalMs) ||
          !isAllowedSkillRange(effect.radius) ||
          !Number.isFinite(effect.damage) ||
          effect.damage <= 0)
      ) {
        errors.push(`隐藏羁绊 ${synergy.id} 的随机单体伤害配置无效`);
      }
    }
  }
  for (const start of HIDDEN_SYNERGIES) {
    const seen = new Set<string>();
    let current: HiddenSynergyDefinition | undefined = start;
    while (current?.repeatableRequirement?.afterSynergyId) {
      if (seen.has(current.id)) {
        errors.push(`计数型隐藏羁绊前置存在循环: ${start.id}`);
        break;
      }
      seen.add(current.id);
      current = HIDDEN_SYNERGIES.find(
        (candidate) =>
          candidate.id === current!.repeatableRequirement!.afterSynergyId,
      );
    }
  }
  for (const effect of HIDDEN_EFFECTS.filter(
    (candidate) => candidate.startsLocked,
  )) {
    if (
      !HIDDEN_SYNERGIES.some((synergy) =>
        synergy.activatesEffectIds.includes(effect.id),
      )
    ) {
      errors.push(`锁定的隐藏效果 ${effect.id} 没有任何激活来源`);
    }
  }
  return [...new Set(errors)];
}

function validateHiddenRuntimeEffect(
  effect: HiddenEffectDefinition["effects"][number],
): string[] {
  const errors: string[] = [];
  if (
    effect.kind === "periodic_non_boss_push_damage" &&
    (!isAllowedAttackIntervalMs(effect.intervalMs) ||
      !isAllowedSkillRange(effect.radius) ||
      effect.pushOutsideRadius !== effect.radius ||
      !Number.isFinite(effect.damage) ||
      effect.damage <= 0)
  ) {
        errors.push("非首领周期伤害/推出范围配置无效");
  }
  if (
    effect.kind === "base_move_speed_multiplier" &&
    (!Number.isFinite(effect.multiplier) || effect.multiplier <= 0)
  ) {
    errors.push("基础移动速度倍率必须大于0");
  }
  if (
    effect.kind === "normal_enemy_taunt_aura" &&
    (!isAllowedSkillRange(effect.radius) || effect.priority !== "standard")
  ) {
    errors.push("普通敌人标准嘲讽配置无效");
  }
  if (
    effect.kind === "every_nth_cast_double" &&
    (!Number.isInteger(effect.every) || effect.every < 2)
  ) {
    errors.push("技能双击周期必须是至少2的整数");
  }
  return errors;
}

function applyOverrides<T extends { id: string }>(
  defaults: readonly T[],
  section: "hiddenEffects" | "hiddenSynergies",
): T[] {
  const input = workshopOverrideJson as unknown as {
    version?: number;
    hiddenEffects?: { removedIds?: unknown; upserts?: unknown };
    hiddenSynergies?: { removedIds?: unknown; upserts?: unknown };
  };
  if (input.version !== 2) {
    return structuredClone(defaults) as T[];
  }
  const patch = input[section];
  const removedIds = new Set(
    Array.isArray(patch?.removedIds)
      ? patch.removedIds.filter((id): id is string => typeof id === "string")
      : [],
  );
  const upserts = new Map(
    (Array.isArray(patch?.upserts) ? patch.upserts : [])
      .filter(
        (entry): entry is T =>
          Boolean(
            entry &&
              typeof entry === "object" &&
              "id" in entry &&
              typeof entry.id === "string",
          ),
      )
      .map((entry) => [entry.id, structuredClone(entry)]),
  );
  const result = defaults.flatMap((entry) =>
    removedIds.has(entry.id)
      ? []
      : [structuredClone(upserts.get(entry.id) ?? entry)],
  );
  const defaultIds = new Set(defaults.map((entry) => entry.id));
  for (const entry of upserts.values()) {
    if (!defaultIds.has(entry.id) && !removedIds.has(entry.id)) {
      result.push(structuredClone(entry));
    }
  }
  return result;
}
